import { canSelectRevisionTemplate } from 'utils/revision-workflow';
import { LoadingOverlay, Box, Card, Divider, Group, Stack, Tooltip } from '@mantine/core';
import { IconLock, IconPlus } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import { RevisionCreatePlanAssignmentRevisionPlanResponse } from 'api/actions/revision-create-plan-assignment-revision-plan/revision-create-plan-assignment-revision-plan-response';
import { RevisionGetPlanAssignmentsResponse } from 'api/actions/revision-get-plan-assignments/revision-get-plan-assignments-response';
import { RevisionGetResponse } from 'api/actions/revision-get/revision-get-response';
import { RevisionSavePlanAssignmentsPayload } from 'api/actions/revision-save-plan-assignments/revision-save-plan-assignments-payload';
import Toast from 'components/Toast';
import DeviceStructureIcon from 'components/DeviceStructureIcon';
import DeviceTypeIcon from 'components/device/DeviceTypeIcon';
import RevisionTermSelect from 'components/inputs/revision-term-select/RevisionTermSelect';
import P1Medium from 'components/typography/P1Medium';
import P1Regular from 'components/typography/P1Regular';
import P2Regular from 'components/typography/P2Regular';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import CreateRevisionPlanModal from 'pages/revisions-module/plan-assignments/CreateRevisionPlanModal';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  DASHBOARD_PAGE_PATH,
  REVISION_PAGE_PATH,
  REVISIONS_PAGE_PATH,
  SELECT_REVISION_TEMPLATE_AND_VISUAL_PAGE_PATH,
} from 'routes/paths';
import { NO_ENTITY_EXISTS_ERROR_CODE, SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

type DeviceDecision = {
  revisionPlanId: string | null;
};

type AssignmentDevice = RevisionGetPlanAssignmentsResponse['devices'][number];
type RevisionPlanOption = AssignmentDevice['availableRevisionPlans'][number];

/**
 * Sorts revision plans so devices keep a stable earliest-date-first order.
 */
function sortRevisionPlans(plans: RevisionPlanOption[]) {
  return [...plans].sort((firstPlan, secondPlan) => {
    if (!firstPlan.date && !secondPlan.date) {
      return 0;
    }

    if (!firstPlan.date) {
      return 1;
    }

    if (!secondPlan.date) {
      return -1;
    }

    return firstPlan.date.localeCompare(secondPlan.date);
  });
}

/**
 * Technician-only page for assigning, skipping, or creating revision plans
 * on revision devices before the revision can be started.
 */
export default function RevisionPlanAssignmentsPage() {
  const navigate = useNavigate();
  const { revisionId } = useParams();
  const { getAction, userId } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [revision, setRevision] = useState<RevisionGetResponse | null>(null);
  const [assignmentState, setAssignmentState] = useState<RevisionGetPlanAssignmentsResponse | null>(null);
  const [decisions, setDecisions] = useState<Record<number, DeviceDecision>>({});
  const [createPlanDevice, setCreatePlanDevice] = useState<AssignmentDevice | null>(null);

  const revisionDetailPath = useMemo(() => REVISION_PAGE_PATH.insert({ revisionId }), [revisionId]);

  const templateSelectionPath = useMemo(
    () => SELECT_REVISION_TEMPLATE_AND_VISUAL_PAGE_PATH.insert({ revisionId }),
    [revisionId]
  );

  const canManageAssignments = useMemo(
    () =>
      !!revision &&
      revision.kind === 'revision' &&
      canSelectRevisionTemplate(revision) &&
      revision.technician?.userId === userId,
    [revision, userId]
  );

  const devicesWithoutAvailablePlans = useMemo(
    () =>
      assignmentState?.devices
        .filter((device) => !device.revisionPlanDecided && device.availableRevisionPlans.length === 0)
        .map((device) => device.deviceName) ?? [],
    [assignmentState]
  );

  useEffect(() => {
    if (!revisionId) {
      return;
    }

    const getRevision = getAction('RevisionGet');
    const getPlanAssignments = getAction('RevisionGetPlanAssignments');

    setLoading(true);

    getRevision({ parameters: { revisionId } })
      .then(async (revisionResponse) => {
        setRevision(revisionResponse);

        if (
          revisionResponse.kind !== 'revision' ||
          !canSelectRevisionTemplate(revisionResponse) ||
          revisionResponse.technician?.userId !== userId
        ) {
          return;
        }

        const planAssignments = await getPlanAssignments({ parameters: { revisionId } });

        setAssignmentState(planAssignments);
        setDecisions(
          planAssignments.devices.reduce<Record<number, DeviceDecision>>((result, device) => {
            result[device.deviceId] = {
              revisionPlanId: device.selectedRevisionPlanId ? String(device.selectedRevisionPlanId) : null,
            };

            return result;
          }, {})
        );
      })
      .catch((e) => {
        if (e?.response?.data?.error?.code === NO_ENTITY_EXISTS_ERROR_CODE) {
          navigate(DASHBOARD_PAGE_PATH.original, { replace: true });
        } else {
          panic(e);
        }
      })
      .finally(() => setLoading(false));
  }, [getAction, navigate, revisionId, userId]);

  /**
   * Updates the selected revision plan for a specific device decision row.
   * Empty value means the device should be saved without a revision plan.
   */
  function setRevisionPlanId(deviceId: number, revisionPlanId: string | null) {
    setDecisions((current) => ({
      ...current,
      [deviceId]: {
        revisionPlanId,
      },
    }));
  }

  /**
   * Persists all device plan decisions before the technician can start the revision.
   */
  function submitAssignments() {
    if (!revisionId || !assignmentState) {
      return;
    }

    const action = getAction('RevisionSavePlanAssignments');

    const payload: RevisionSavePlanAssignmentsPayload = {
      devices: assignmentState.devices
        .filter((device) => !device.revisionPlanDecided)
        .map((device) => {
          const decision = decisions[device.deviceId];
          const revisionPlanId = decision?.revisionPlanId ?? null;

          return {
            deviceId: device.deviceId,
            noRevisionPlan: revisionPlanId === null,
            revisionPlanId: revisionPlanId ? Number(revisionPlanId) : undefined,
          };
        }),
    };

    setSaving(true);

    action({
      parameters: { revisionId },
      payload,
    })
      .then(() => {
        notifications.show({
          title: 'Termíny zariadení boli uložené',
          message: 'Môžete pokračovať na výber šablóny revíznej správy.',
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        navigate(templateSelectionPath, { replace: true });
      })
      .catch(panic)
      .finally(() => setSaving(false));
  }

  if (revision && !canManageAssignments) {
    return <Navigate to={revisionDetailPath} replace />;
  }

  if (assignmentState?.allDevicesDecided) {
    return <Navigate to={templateSelectionPath} replace />;
  }

  return (
    <DashboardLayout
      title="Priraďte termíny zariadeniam"
      breadcrumbs={[
        { title: 'Revízne správy', link: REVISIONS_PAGE_PATH.original },
        { title: revision?.revisionName ?? 'Načítavam' },
        { title: 'Termíny zariadení' },
      ]}
    >
      <LoadingOverlay visible={loading || saving} />
      {revision && assignmentState && (
        <Card p={24}>
          <Stack spacing={24}>
            <Stack spacing={8}>
              <P1Medium color="gray.9">Pred začatím revízie rozhodnite termín pre každé zariadenie.</P1Medium>
              <P2Regular color="gray.6">
                Vyberte existujúci termín, vytvorte nový termín alebo ponechajte pole prázdne, ak sa zariadenie má
                uložiť bez termínu revízie.
              </P2Regular>
            </Stack>

            {devicesWithoutAvailablePlans.length > 0 && (
              <Toast
                type="warning"
                fullWidth
                withCloseButton={false}
                message={
                  devicesWithoutAvailablePlans.length === 1
                    ? `Zariadenie ${devicesWithoutAvailablePlans[0]} zatiaľ nemá dostupné žiadne termíny. Môžete vytvoriť nový termín alebo ho uložiť bez termínu revízie.`
                    : `Niektoré zariadenia nemajú dostupné žiadne termíny (${devicesWithoutAvailablePlans.join(
                        ', '
                      )}). Pre tieto zariadenia môžete vytvoriť nové termíny alebo ich uložiť bez termínu revízie.`
                }
              />
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'max-content minmax(280px, 520px)',
                columnGap: '24px',
                rowGap: '20px',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              {assignmentState.devices.map((device) => {
                const decision = decisions[device.deviceId] ?? { revisionPlanId: null };
                const isLocked = device.revisionPlanDecided;
                const hasAvailablePlans = device.availableRevisionPlans.length > 0;

                return (
                  <Fragment key={device.deviceId}>
                    <Group spacing={8} align="center" noWrap>
                      <DeviceTypeIcon typeId={device.deviceTypeId} />
                      <Group spacing={4} noWrap align="center">
                        {device.parentDevice && (
                          <P1Regular color="gray.5">{device.parentDevice.deviceName} /</P1Regular>
                        )}
                        <P1Regular fw={device.structure === 'parent' ? 500 : 400}>{device.deviceName}</P1Regular>
                      </Group>
                      <DeviceStructureIcon structure={device.structure} />
                    </Group>

                    <Box maw={520} w="100%">
                      <Group align="center" spacing={12} noWrap>
                        <Box sx={{ flex: '1 1 auto' }}>
                          <RevisionTermSelect
                            options={device.availableRevisionPlans}
                            autoSelectSingleResult={!isLocked}
                            clearable={!isLocked}
                            readOnly={isLocked || !hasAvailablePlans}
                            searchable={!isLocked}
                            rightSection={
                              isLocked ? (
                                <Tooltip
                                  label="Výber termínu pre toto zariadenie je uzamknutý, preto ho už nie je možné zmeniť."
                                  withArrow
                                >
                                  <Box c="gray.6" sx={{ display: 'flex', alignItems: 'center' }}>
                                    <IconLock size={16} stroke={1.8} />
                                  </Box>
                                </Tooltip>
                              ) : undefined
                            }
                            styles={isLocked ? { rightSection: { pointerEvents: 'all' } } : undefined}
                            placeholder={
                              isLocked && decision.revisionPlanId === null
                                ? 'Bez termínu revízie'
                                : hasAvailablePlans
                                ? 'Vyberte termín alebo ponechajte prázdne'
                                : 'Bez dostupných termínov'
                            }
                            value={decision.revisionPlanId}
                            onChange={(value) => {
                              if (!isLocked) {
                                setRevisionPlanId(device.deviceId, value);
                              }
                            }}
                          />
                        </Box>

                        {!isLocked && (
                          <Button
                            variant="tertiary"
                            size="sm"
                            leftIcon={<IconPlus />}
                            onClick={() => setCreatePlanDevice(device)}
                          >
                            Nový termín
                          </Button>
                        )}
                      </Group>
                    </Box>
                  </Fragment>
                );
              })}
            </Box>

            <Divider color="gray.1" />

            <Group position="right">
              <Button onClick={submitAssignments} loading={saving}>
                Uložiť a pokračovať
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      <CreateRevisionPlanModal
        opened={createPlanDevice !== null}
        deviceName={createPlanDevice?.deviceName ?? ''}
        loading={creatingPlan}
        onClose={() => {
          if (!creatingPlan) {
            setCreatePlanDevice(null);
          }
        }}
        onSubmit={async (payload) => {
          if (!createPlanDevice || !revisionId) {
            return;
          }

          const action = getAction('RevisionCreatePlanAssignmentRevisionPlan');

          setCreatingPlan(true);

          try {
            const response: RevisionCreatePlanAssignmentRevisionPlanResponse = await action({
              parameters: {
                revisionId,
                deviceId: String(createPlanDevice.deviceId),
              },
              payload,
            });

            const createdRevisionPlanId = response.revisionPlan.revisionPlanId;

            if (!createdRevisionPlanId) {
              throw new Error('The created revision plan is missing an identifier');
            }

            setAssignmentState((current) => {
              if (!current) {
                /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
                return current;
              }

              return {
                ...current,
                devices: current.devices.map((device) => {
                  if (device.deviceId !== createPlanDevice.deviceId) {
                    return device;
                  }

                  const availableRevisionPlans = sortRevisionPlans([
                    ...device.availableRevisionPlans.filter(
                      (revisionPlan) => revisionPlan.revisionPlanId !== createdRevisionPlanId
                    ),
                    response.revisionPlan,
                  ]);

                  return {
                    ...device,
                    availableRevisionPlans,
                  };
                }),
              };
            });

            setRevisionPlanId(createPlanDevice.deviceId, String(createdRevisionPlanId));
            setCreatePlanDevice(null);

            notifications.show({
              title: 'Termín bol vytvorený',
              message: `Nový termín pre zariadenie ${createPlanDevice.deviceName} je pripravený na uloženie.`,
              color: SUCCESS_NOTIFICATION_COLOR,
            });
          } catch (error) {
            panic(error instanceof Error ? error : new Error(String(error)));
            throw error;
          } finally {
            setCreatingPlan(false);
          }
        }}
      />
    </DashboardLayout>
  );
}
